import template from './dashboard-report.component.html';
import { IAugmentedJQuery, IComponentController, ITimeoutService, IOnChangesObject } from 'angular';
import type { Dashboard, DashboardService, WidgetInstance } from '../dashboard.service';
import type { DashboardFiltersValues } from '../dashboard-filters.service';
import type { HttpService } from '../../App/services/http-service';
import { GridStack } from 'gridstack';

export class DashboardReportController implements IComponentController {
	public id = '';
	public dashboardLoader?: Promise<Dashboard>;
	public navigationId!: string;
	public filters?: DashboardFiltersValues;
	public pageHeight?: number;
	private resizeObserver: ResizeObserver;
	private observedMap = new Map<Element, WidgetInstance>();
	private dashboard?: Dashboard;
	public onLoaded!: () => void;
	private noMxLoaders = false;
	private allAjaxComplete = false;
	private loadingTimer?: number;
	private maxLoadingTime = 60000; // 1 minute
	private loaded = false;

	static readonly $inject = ['$element', '$timeout', 'mx.sb.Dashboard.Service', 'mx.SolutionBuilderAgent.Http'];
	constructor(
		private readonly $element: IAugmentedJQuery,
		private readonly $timeout: ITimeoutService,
		private readonly dashboardService: DashboardService,
		private readonly httpService: HttpService
	) {
		this.resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const widgetInstance = this.observedMap.get(entry.target);
				if (widgetInstance?.el) {
					GridStack.resizeToContentCB!(widgetInstance.el);
				}
			}
			this.applyPageHeight(this.pageHeight);
		});
	}

	$onInit() {
		this.dashboardLoader = this.dashboardService.getDashboard(this.navigationId, true, this.filters);
		this.id = `report-${Date.now()}`;
	}

	$onDestroy() {
		this.resizeObserver.disconnect();
	}

	$onChanges(onChangesObj: IOnChangesObject) {
		if (onChangesObj.pageHeight) {
			this.applyPageHeight(this.pageHeight);
		}
	}

	private onInitialized(dashboard: Dashboard) {
		this.dashboard = dashboard;

		dashboard.widgetInstances.forEach((widgetInstance) => {
			if (widgetInstance.el) {
				widgetInstance.el.style.order = widgetInstance.priority.toString();

				if (!widgetInstance.hidden && widgetInstance.dataModel.Context.Stretchable) {
					widgetInstance.el.classList.add('print-stretchable');
					const observedEl = widgetInstance.el.querySelector('.dashboard-widget--content');
					if (observedEl) {
						this.observedMap.set(observedEl, widgetInstance);
						this.resizeObserver.observe(observedEl);
					}
				}
			}
		});

		this.$timeout(() => {
			this.applyPageHeight(this.pageHeight);
		});

		// Handle Loading state:
		// 1) all requests should be complete
		this.httpService.waitForAllRequests(2000, this.maxLoadingTime).then(() => {
			this.allAjaxComplete = true;
			this.checkIfLoaded();
		});
		// 2) no visible mx-loading indicators
		this.checkLoaders();
		// 3) but wait not longer than maxLoadingTime
		this.loadingTimer = window.setTimeout(() => this.checkIfLoaded(true), this.maxLoadingTime);
	}

	private applyPageHeight(pageHeight?: number) {
		if (!this.dashboard || !this.dashboard.widgetInstances.length) {
			return;
		}

		this.dashboard.widgetInstances.forEach((widgetInstance) => {
			if (widgetInstance.el) {
				widgetInstance.el.style.marginTop = '';
			}
		});

		if (typeof pageHeight !== 'number') {
			return;
		}

		const rect = this.$element[0].getBoundingClientRect();
		const scale = rect.width / this.$element[0].offsetWidth;

		const documentStartY = rect.y;

		let page = 1;
		let brokenWidgetInstances: WidgetInstance[] = [];
		do {
			const pageStartY = pageHeight * (page - 1) + documentStartY;

			brokenWidgetInstances = this.dashboard.widgetInstances.filter((widgetInstance) => {
				if (widgetInstance.el) {
					const rect = widgetInstance.el.getBoundingClientRect();
					const widgetYFromOnPage = rect.y - pageStartY;
					const widgetYToOnPage = widgetYFromOnPage + rect.height;

					const pageBreak = pageHeight > widgetYFromOnPage && pageHeight < widgetYToOnPage && rect.height <= pageHeight;
					if (pageBreak) {
						const marginTop = (pageHeight - widgetYFromOnPage) / scale;
						widgetInstance.el.style.marginTop = `${marginTop.toString()}px`;
					}

					return pageBreak;
				}
				return false;
			});
		} while (brokenWidgetInstances.length > 0 && page++ < 100);
	}

	private checkLoaders() {
		if (this.noMxLoaders || this.loaded) {
			return;
		}
		if (!this.$element.find('.mx-loading:visible, .mx-loading-bg:visible').length) {
			this.noMxLoaders = true;
			this.checkIfLoaded();
			return;
		} else {
			setTimeout(() => this.checkLoaders(), 500);
		}
	}

	private checkIfLoaded(forceLoadedState: boolean = false) {
		if (forceLoadedState || (this.noMxLoaders && this.allAjaxComplete)) {
			clearTimeout(this.loadingTimer);
			this.$timeout(() => {
				this.applyPageHeight(this.pageHeight);
				this.loaded = true;
				this.onLoaded();
			});
		}
	}
}

export const DashboardReportComponent = {
	name: 'mxDashboardReport',
	controllerAs: 'vm',
	controller: DashboardReportController,
	bindings: {
		navigationId: '<',
		filters: '<',
		pageHeight: '<',
		onLoaded: '&',
	},
	template,
};
